import { Info } from 'lucide-react';
import { ReactElement, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  cn,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Inline,
  Stack,
  Switch,
  Typography,
  withTooltip,
} from '@crossnokaye/ux-common';
import { InputInlineValueChangeLabel } from '@crossnokaye/ux-common/ckds';

import { EditSettingsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { getSourceUnits } from '@/features/equipment-browser/utils';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

const SwitchWithTooltip = withTooltip(Switch);
const InfoTooltip = withTooltip(Info);

interface SettingsFieldSwitchProps {
  setting: EnhancedSetting;
  form: UseFormReturn<EditSettingsFormData>;
}

const TooltipContent = ({
  originalValue,
  currentValue,
}: {
  originalValue: boolean;
  currentValue: boolean;
}): ReactElement => {
  return (
    <div
      className={
        'm-0 flex items-center justify-center gap-0.5 whitespace-nowrap text-ckds-input-fg-new'
      }
    >
      {currentValue.toString()}
      <InputInlineValueChangeLabel
        className={'text-muted-foreground'}
        units={''}
        oldValue={originalValue.toString()}
      />
    </div>
  );
};

/**
 * Converts a form string value to a boolean for the Switch component.
 * Handles only formatted values like "True" for now.
 */
const stringToBoolean = (value: string | boolean | undefined): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    return lower === 'true';
  }
  return false;
};

/**
 * Switch field component for boolean settings.
 * Shows change indicator when value differs from original.
 */
export const SettingFieldSwitch = ({ setting, form }: SettingsFieldSwitchProps): ReactElement => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  // Get the formatted labels for the original setting value
  const originalLabels = useMemo(() => {
    const sourceUnits = getSourceUnits(setting);
    return formatUnits({ value: setting?.value, units: sourceUnits }, setting?.displayUnits);
  }, [setting]);

  return (
    <FormField
      control={form.control}
      name={setting.ref.id ? `settings.${setting.ref.id}` : ''}
      render={({ field }) => {
        const hasChanged = field?.value?.toString() !== originalLabels.valueLabel;
        const isChecked = stringToBoolean(field.value);

        // Convert boolean back to the formatted string value when switch changes
        const handleChange = (checked: boolean) => {
          const sourceUnits = getSourceUnits(setting);
          const formatted = formatUnits(
            { value: checked, units: sourceUnits },
            setting?.displayUnits
          );
          field.onChange(formatted.valueLabel);
        };

        return (
          <FormItem>
            <Stack gap="xs">
              <Inline gap="sm" className="text-sm font-medium text-muted-foreground">
                {setting.label}
                <InfoTooltip
                  size={16}
                  className="text-muted-foreground cursor-pointer hover:text-ckds-text-fg-tier2"
                  tooltipContent={
                    <Stack className="max-w-96">
                      <Typography variant="h4">{setting.label}</Typography>
                      <Typography variant="body">{setting.description}</Typography>
                    </Stack>
                  }
                />
              </Inline>
              <FormControl>
                <div
                  className={cn(
                    'inline-block',
                    hasChanged && 'ring-2 ring-ckds-base-caution p-0.5'
                  )}
                >
                  <SwitchWithTooltip
                    disabled={disabledByPermissions}
                    checked={isChecked}
                    onCheckedChange={handleChange}
                    tooltipContent={
                      hasChanged ? (
                        <TooltipContent
                          originalValue={stringToBoolean(setting.value?.toString() ?? 'false')}
                          currentValue={isChecked}
                        />
                      ) : disabledByPermissions ? (
                        tooltipText
                      ) : undefined
                    }
                  />
                </div>
              </FormControl>
            </Stack>
            <FormMessage className={'pl-2'} />
          </FormItem>
        );
      }}
    />
  );
};
